import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import SEO from 'components/shared/SEO';
import ButtonPrimary from 'components/shared/ButtonPrimary';
import ButtonText from 'components/shared/ButtonText';
import ButtonShowPassword from 'components/shared/ButtonShowPassword';
import Field from 'components/shared/Field';
import FormError from 'components/shared/FormError';
import Row from 'components/shared/Row';
import Text from 'components/shared/Text';
import Heading from 'components/shared/Heading';
import Spinner from 'components/shared/Spinner';
import { SocialLogin } from 'components/app/SocialLogin';
import ERRORS from 'constants/errors';
import { clearError, signUpWithEmail } from 'reduxConfig/auth.creators';
import { someValuesTruthy } from 'helpers/objects';
import { validateEmail } from 'helpers/validation';
import { getURLParam, deleteOrSetURLParam } from 'helpers/url';
import { checkPasswordStrength } from 'helpers/passwords';
import { NBXReferral } from 'services';
import STEPS from 'constants/steps';
import styles from './styles.module.scss';

const microapps = window.MICROFRONTENDS_CONFIG;

function Signup({
  dispatch,
  error: apiError,
  loading,
  step,
  loadingReferral,
  isReferralCodeParamValid,
  referralCodeParam,
  setReferrer,
  cardPartnershipCode
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState({
    email: '',
    password: '',
    passwordTwo: '',
    referralCode: referralCodeParam,
    acceptedTerms: false
  });

  const [passwordScore, setPasswordScore] = useState(0);
  const [passwordPwned, setPasswordPwned] = useState(null);
  const [errors, setErrors] = useState(() => ({
    email: '',
    password: '',
    passwordTwo: '',
    referralCode: isReferralCodeParamValid || !referralCodeParam ? '' : t('auth_signup_invalid_referral')
  }));
  const [inputTypes, setInputTypes] = useState({
    password: 'password',
    passwordTwo: 'password'
  });
  const { email, password, passwordTwo, acceptedTerms } = values;
  const [signupMethod, setSignupMethod] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState();
  const [showReferralCode, setShowReferralCode] = useState();

  // Fill email field if 'email' query param is provided
  useEffect(() => {
    const queryEmail = getURLParam('email');
    if (queryEmail) {
      setValues({ ...values, email: queryEmail });
      setErrors({ ...errors, email: validateEmail(queryEmail) });
    }
  }, []); // eslint-disable-line

  const handleEmailChange = e => {
    if (apiError) dispatch(clearError());
    setValues({ ...values, email: e.target.value });
    setErrors({
      ...errors,
      email: validateEmail(e.target.value),
      password: validatePasswordOne(values.password)
    });
  };

  const handlePasswordChange = e => {
    if (apiError) dispatch(clearError());
    setValues({ ...values, password: e.target.value });
    setErrors({
      ...errors,
      password: validatePasswordOne(e.target.value),
      passwordTwo:
        values.passwordTwo.length > 0
          ? validatePasswordTwo(values.passwordTwo, e.target.value)
          : errors.passwordTwo
    });
  };

  const handlePasswordTwoChange = e => {
    if (apiError) dispatch(clearError());
    setValues({ ...values, passwordTwo: e.target.value });
    setErrors({ ...errors, passwordTwo: validatePasswordTwo(e.target.value, values.password) });
  };

  const handleReferralChange = async ({ target: { value } }) => {
    setValues({ ...values, referralCode: value });
    await validateReferralCode(value);
  };

  const validateReferralCode = async code => {
    if (code === '') {
      setErrors({ ...errors, referralCode: '' });
      deleteOrSetURLParam('referral', null);
    } else {
      const { valid, name } = await NBXReferral.validate(code);

      if (valid) {
        setReferrer(name);
        deleteOrSetURLParam('referral', code);
      } else {
        deleteOrSetURLParam('referral', null);
      }

      setErrors({
        ...errors,
        referralCode: valid ? '' : t('auth_signup_invalid_referral')
      });
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    setSignupMethod('email');

    const submissionErrors = {
      email: validateEmail(email),
      password: validatePasswordOne(password),
      passwordTwo: validatePasswordTwo(passwordTwo, password)
    };

    if (someValuesTruthy(submissionErrors)) setErrors(submissionErrors);
    else dispatch(signUpWithEmail(email, password, values.referralCode || null, cardPartnershipCode || null));
  };

  const disableSubmit = () => {
    if (!email || !password || !passwordTwo || passwordPwned !== false || passwordScore < 4 || !acceptedTerms)
      return true;
    if (someValuesTruthy(errors)) return true;
    if (loading && signupMethod === 'email') return true;
    return false;
  };

  const setPasswordInputType = key => {
    const type = inputTypes[key];
    const newInputType = type === 'text' ? 'password' : 'text';
    setInputTypes({ ...inputTypes, [key]: newInputType });
  };

  const validatePasswordOne = value => {
    if (value.length === 0) {
      return '';
    }
    const { error, score, hibpPromise } = checkPasswordStrength(value, values.email);
    setPasswordScore(score);
    if (hibpPromise) {
      hibpPromise
        .then(isPwned => {
          setPasswordPwned(isPwned);
          if (isPwned) {
            setErrors({ ...errors, password: ERRORS.passwordCompromised });
          }
        })
        .catch(err => {
          console.error('HIBP error', err); // eslint-disable-line
          setPasswordPwned(false);
        });
    }
    return error;
  };

  const validatePasswordTwo = (value, currentPassword) => {
    if (value !== currentPassword) return ERRORS.passwordsNotEqual;
    return null;
  };

  if (step === STEPS.verifyCode || step === STEPS.verifySMSCode)
    return <Redirect to={microapps.routes.verifyCode.path} />;
  if (step === STEPS.verifyEmail) return <Redirect to={microapps.routes.verifyEmail.path} />;
  if (step === STEPS.addPhoneNumber) return <Redirect to={microapps.routes.addPhoneNumber.path} />;

  if (loadingReferral) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner delayed />
      </div>
    );
  }

  return (
    <>
      <SEO
        title={t('auth_signup_meta_title')}
        description={t('auth_signup_meta_description')}
        canonicalLink="https://app.nbx.com/login/signup"
      />
      {!isReferralCodeParamValid && <Heading tag="h1">{t('auth_signup_join_nbx')}</Heading>}

      <span id="recaptcha-verifier"></span>

      <Text tag="p" muted>
        {t('auth_signup_subtitle')}
      </Text>

      <Text tag="p">{t('tos_modal_review_n_accept')}</Text>

      {cardPartnershipCode && <nbx-text>Partner shop: {cardPartnershipCode.substring(0, 200)}</nbx-text>}

      {!cardPartnershipCode && isReferralCodeParamValid && (
        <>
          <Text tag="p" muted>
            <Trans i18nKey="auth_signup_invited_explain_1">
              You will receive 50 NOK worth of BTC when you are a verified customer of NBX!
            </Trans>
          </Text>

          <Text tag="p" muted>
            <Trans i18nKey="auth_signup_invited_explain_2">
              The person that referred you will get 150 NOK worth of BTC once you are a verified customer of
              NBX. In addition NBX gives back 20% of the trading fees you pay in commission to the person
              referring you.
            </Trans>
          </Text>

          <Text tag="p" muted>
            <Trans i18nKey="auth_signup_invited_explain_3">
              Once you are a customer you can refer others as well!
            </Trans>
          </Text>
        </>
      )}

      <div className={styles.acceptTermsField}>
        <nbx-checkbox
          checked={acceptedTerms}
          onClick={() => {
            setValues({
              ...values,
              acceptedTerms: !acceptedTerms
            });
          }}>
          <nbx-text muted={!acceptedTerms} slot="label">
            <Trans i18nKey="auth_signup_TOS">
              <nbx-button-text
                underlined
                href="https://nbx.com/en/terms-of-service"
                onClick={e => e.stopPropagation()}
              />
            </Trans>
          </nbx-text>
        </nbx-checkbox>
      </div>

      {!cardPartnershipCode && !(isReferralCodeParamValid && referralCodeParam) && (
        <div className={styles.referralCode}>
          {showReferralCode || (!isReferralCodeParamValid && referralCodeParam) ? (
            <div className={styles.referralCodeInput}>
              <Field
                id="referral"
                noLabel={true}
                placeholder={t('referral_code')}
                name={t('referral_code')}
                value={values.referralCode}
                error={errors.referralCode}
                onBlur={handleReferralChange}
                onChange={handleReferralChange}
              />
              <Text tag="p" muted styles={[styles.belowHeaderText]}>
                <ButtonText onClick={() => setShowReferralCode(false)}>
                  <nbx-icon icon="close" />
                </ButtonText>
              </Text>
            </div>
          ) : (
            <Text tag="p" muted styles={[styles.belowHeaderText]}>
              <ButtonText onClick={() => setShowReferralCode(true)}>Have a referral code?</ButtonText>
            </Text>
          )}
        </div>
      )}

      {!showEmailForm && (
        <>
          <div className={styles.signUpSocialWrapper}>
            <SocialLogin
              dispatch={dispatch}
              loginMethod={signupMethod}
              setLoginMethod={setSignupMethod}
              loading={loading}
              disabled={!acceptedTerms}
              referralCode={values.referralCode}
              cardPartnershipCode={cardPartnershipCode}
              isLogin={false}
            />
          </div>
          <Row>
            <ButtonPrimary
              onClick={() => setShowEmailForm(true)}
              loading={loading && signupMethod === 'email'}
              disabled={!acceptedTerms}
              fullWidth
              styles={[styles.marginBottom, styles.signUpWithEmailBtn]}>
              <img src="/assets/icons/email.svg" alt="" />
              <span>
                {t('common_sign_up')} {t('common_with')} {t('common_email')}
              </span>
            </ButtonPrimary>
          </Row>
        </>
      )}

      {showEmailForm && (
        <>
          <form onSubmit={handleSubmit}>
            <Field
              id="email"
              name={t('common_email')}
              type="email"
              value={email}
              error={errors.email}
              onBlur={handleEmailChange}
              onChange={handleEmailChange}
            />

            <div className={styles.signUpPasswordsWrapper}>
              <div className={styles.signUpPasswordField}>
                <Field
                  id="password"
                  name={t('common_password')}
                  value={password}
                  error={errors.password}
                  type={inputTypes.password}
                  showPasswordStrength
                  passwordStrength={passwordScore}
                  passwordCompromised={passwordPwned}
                  onBlur={handlePasswordChange}
                  onChange={handlePasswordChange}
                  inlineElements={<ButtonShowPassword onClick={() => setPasswordInputType('password')} />}
                />
              </div>

              <div className={styles.signUpPasswordField}>
                <Field
                  id="passwordTwo"
                  name={t('common_repeat_password')}
                  placeholder="Password"
                  value={passwordTwo}
                  error={errors.passwordTwo}
                  type={inputTypes.passwordTwo}
                  onBlur={handlePasswordTwoChange}
                  onChange={handlePasswordTwoChange}
                  inlineElements={<ButtonShowPassword onClick={() => setPasswordInputType('passwordTwo')} />}
                />
              </div>
            </div>

            {apiError && <FormError>{t(apiError)}</FormError>}

            <Row>
              <ButtonPrimary
                disabled={disableSubmit()}
                loading={loading && signupMethod === 'email'}
                type="submit"
                fullWidth
                styles={[styles.marginBottom]}>
                {t('common_sign_up')}
              </ButtonPrimary>
            </Row>
          </form>
        </>
      )}

      <Text tag="p" muted styles={[styles.belowHeaderText]}>
        {t('auth_signup_login')}{' '}
        <ButtonText to={microapps.routes.login.path}>{t('common_log_in')}</ButtonText>
      </Text>

      {showEmailForm && (
        <ButtonText onClick={() => setShowEmailForm(false)} styles={[styles.buttonCancel]}>
          <span className={styles.arrowback}>
            <nbx-icon icon="arrowdown" />
          </span>
          {t('common_back')}
        </ButtonText>
      )}
    </>
  );
}

Signup.propTypes = {
  dispatch: PropTypes.func.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  step: PropTypes.string,
  loadingReferral: PropTypes.bool,
  validReferral: PropTypes.bool,
  referralCode: PropTypes.string,
  setReferrer: PropTypes.func,
  cardPartnershipCode: PropTypes.string
};

Signup.defaultProps = {
  error: null,
  step: null,
  loadingReferral: false,
  validReferral: false,
  referralCode: null,
  setReferrer: () => {},
  cardPartnershipCode: null
};

export default Signup;
